/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array, fragmentArray } from 'ember-data-model-fragments/attributes';
import type ReportTemplateSection from '@linc-technologies/linced-common/models/reports/report-template-section';
import type StudentReport from '@linc-technologies/linced-common/models/reports/student-report';
import type StudentReportSectionElement from '@linc-technologies/linced-common/models/reports/student-report-section-element';
import type School from '@linc-technologies/linced-common/models/school';

// Proto: reports.StudentReportSection.
export default class StudentReportSection extends Model {
  declare [Type]: 'reports/student-report-section';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @belongsTo('reports/student-report', { async: true, inverse: null })
  declare studentReportId: AsyncBelongsTo<StudentReport>;

  @alias('studentReportId')
  declare studentReport: AsyncBelongsTo<StudentReport>;

  @alias('studentReport.id')
  declare student_report_id: string | null | undefined;

  @belongsTo('reports/report-template-section', { async: true, inverse: null })
  declare reportTemplateSectionId: AsyncBelongsTo<ReportTemplateSection>;

  @alias('reportTemplateSectionId')
  declare reportTemplateSection: AsyncBelongsTo<ReportTemplateSection>;

  @alias('reportTemplateSection.id')
  declare report_template_section_id: string | null | undefined;

  @alias('reportTemplateSection.sectionType')
  declare sectionType: unknown;

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('string', { defaultValue: '' })
  declare strandSliderVisual: string;

  @attr('string', { defaultValue: '' })
  declare strandTableVisual: string;

  @attr('string', { defaultValue: '' })
  declare progressVisual: string;

  // === Publish-time snapshot (HERO-13205) ===
  // JSON blob populated by the backend at publish time. Carries the
  // per-component resolved data the renderer needs so a published report
  // can render without loading school / person / peopleSchema / goals /
  // assessments / etc. records. Null on draft / review / pre-13205 published
  // sections — renderer falls back to the live-data path in that case.
  // Contract: docs/reporting/publish-snapshot.md
  @attr('json')
  declare publishedSnapshot: unknown;

  @attr('boolean', { defaultValue: false })
  declare complete: boolean;

  @attr('string', { defaultValue: '' })
  declare judgement: string;

  @fragmentArray('reports/student-report-section-element')
  declare elementProgressions: FragmentArray<StudentReportSectionElement>;

  @attr('string', { defaultValue: '' })
  declare effort: string;

  @attr('string', { defaultValue: '' })
  declare narrative: string;

  @attr('string', { defaultValue: '' })
  declare nextSteps: string;

  @attr('string', { defaultValue: '' })
  declare homeHelp: string;

  // Goals listed as next steps, kept apart from nextSteps (rubric-generated
  // or teacher-written) so each can change without touching the other.
  @attr('string', { defaultValue: '' })
  declare nextStepsGoals: string;

  // True while the field still holds untouched rubric-generated text. Slider
  // changes only regenerate a field while this is set (or it's empty).
  @attr('boolean', { defaultValue: false })
  declare narrativeAutoGenerated: boolean;

  @attr('boolean', { defaultValue: false })
  declare nextStepsAutoGenerated: boolean;

  @attr('boolean', { defaultValue: false })
  declare homeHelpAutoGenerated: boolean;

  @array('string')
  declare assignedTo: StatefulArray<string>;

  // Id of the user who last edited this section.
  @attr('string', { defaultValue: '' })
  declare lastUpdatedBy: string;

  // The next-steps block as rendered on the report: the rubric /
  // teacher-written next steps, then goals.
  @computed('nextSteps', 'nextStepsGoals')
  get nextStepsDisplay() {
    return [this.nextSteps, this.nextStepsGoals]
      .filter((html) => html && html.replace(/<[^>]*>/g, '').trim().length)
      .join('');
  }
}
